import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
} from '@mui/material';
import Layout from '../../components/Layout';
import api from '../../services/api';

const WIALON_SYNC_INTERVAL_OPTIONS = [
  { value: 15, label: 'Every 15 seconds' },
  { value: 30, label: 'Every 30 seconds (default)' },
  { value: 60, label: 'Every 1 minute' },
  { value: 120, label: 'Every 2 minutes' },
  { value: 300, label: 'Every 5 minutes' },
];

const ManagerOrganization = () => {
  const [organization, setOrganization] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    tax_id: '',
    wialon_gps_enabled: false,
    wialon_access_token: '',
    wialon_sync_interval_seconds: 30,
  });
  const [wialonTokenConfigured, setWialonTokenConfigured] = useState(false);
  const [testingWialon, setTestingWialon] = useState(false);
  const [syncingWialon, setSyncingWialon] = useState(false);
  const [wialonMessage, setWialonMessage] = useState('');
  const [wialonPreview, setWialonPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      const response = await api.get('/organizations/organizations/profile/');
      setOrganization(response.data);
      setFormData({
        name: response.data.name || '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip_code: response.data.zip_code || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        tax_id: response.data.tax_id || '',
        wialon_gps_enabled: Boolean(response.data.wialon_gps_enabled),
        wialon_access_token: '',
        wialon_sync_interval_seconds: response.data.wialon_sync_interval_seconds || 30,
      });
      setWialonTokenConfigured(Boolean(response.data.wialon_token_configured));
    } catch (error) {
      console.error('Error fetching organization:', error);
      // If organization doesn't exist, show form to create it
      if (error.response?.status === 404) {
        setMessage('Please complete your organization information below.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const buildOrganizationPayload = () => {
    const payload = { ...formData };
    if (!payload.wialon_access_token) {
      delete payload.wialon_access_token;
    }
    return payload;
  };

  const handleTestWialon = async () => {
    setTestingWialon(true);
    setWialonMessage('');
    setWialonPreview(null);
    try {
      const body = {};
      if (formData.wialon_access_token.trim()) {
        body.wialon_access_token = formData.wialon_access_token.trim();
      }
      const response = await api.post('/organizations/organizations/test-wialon/', body);
      setWialonMessage(response.data.message || 'Wialon connection successful.');
      setWialonPreview(response.data);
    } catch (error) {
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Could not connect to Wialon.';
      setWialonMessage(errorMsg);
    } finally {
      setTestingWialon(false);
    }
  };

  const handleSyncWialon = async () => {
    setSyncingWialon(true);
    setWialonMessage('');
    try {
      const response = await api.post('/organizations/organizations/sync-wialon/');
      setWialonMessage(response.data.message || 'Wialon GPS sync completed.');
      setWialonPreview(response.data);
    } catch (error) {
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Wialon sync failed.';
      setWialonMessage(errorMsg);
    } finally {
      setSyncingWialon(false);
    }
  };

  const handleSaveWialonSettings = async () => {
    if (!organization) {
      setWialonMessage('Save organization details first, then configure Wialon GPS.');
      return;
    }
    setSaving(true);
    setWialonMessage('');
    try {
      const payload = {
        wialon_gps_enabled: formData.wialon_gps_enabled,
        wialon_sync_interval_seconds: Number(formData.wialon_sync_interval_seconds) || 30,
      };
      if (formData.wialon_access_token.trim()) {
        payload.wialon_access_token = formData.wialon_access_token.trim();
      }
      await api.put('/organizations/organizations/profile/', payload);
      setWialonMessage('Wialon settings saved.');
      setFormData((prev) => ({ ...prev, wialon_access_token: '' }));
      fetchOrganization();
    } catch (error) {
      setWialonMessage(
        error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to save Wialon settings.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      // Use PUT if organization exists, POST if it doesn't
      const method = organization ? 'put' : 'post';
      await api[method]('/organizations/organizations/profile/', buildOrganizationPayload());
      setMessage('Organization saved successfully');
      fetchOrganization();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error saving organization. Please check your input.';
      setMessage(errorMsg);
      console.error('Error saving organization:', error.response?.data || error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <Typography>Loading...</Typography>
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Typography variant="h4" gutterBottom>
          Organization Details
        </Typography>
        
        {organization && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Current Organization Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Organization Name
                </Typography>
                <Typography variant="body1">{organization.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">{organization.email || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Phone
                </Typography>
                <Typography variant="body1">{organization.phone || 'Not provided'}</Typography>
              </Grid>
              {organization.tax_id && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Tax ID / EIN
                  </Typography>
                  <Typography variant="body1">{organization.tax_id}</Typography>
                </Grid>
              )}
              {(organization.address || organization.city || organization.state || organization.zip_code) && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {[
                      organization.address,
                      organization.city,
                      organization.state,
                      organization.zip_code
                    ].filter(Boolean).join(', ') || 'Not provided'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {organization ? 'Update Organization' : 'Create Organization'}
          </Typography>
          
          {message && (
            <Alert 
              severity={message.includes('successfully') ? 'success' : 'error'} 
              sx={{ mb: 2 }}
              onClose={() => setMessage('')}
            >
              {message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organization Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth required>
                  <InputLabel>State</InputLabel>
                  <Select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    label="State"
                    required
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    <MenuItem value="AL">AL - Alabama</MenuItem>
                    <MenuItem value="AK">AK - Alaska</MenuItem>
                    <MenuItem value="AZ">AZ - Arizona</MenuItem>
                    <MenuItem value="AR">AR - Arkansas</MenuItem>
                    <MenuItem value="CA">CA - California</MenuItem>
                    <MenuItem value="CO">CO - Colorado</MenuItem>
                    <MenuItem value="CT">CT - Connecticut</MenuItem>
                    <MenuItem value="DE">DE - Delaware</MenuItem>
                    <MenuItem value="FL">FL - Florida</MenuItem>
                    <MenuItem value="GA">GA - Georgia</MenuItem>
                    <MenuItem value="HI">HI - Hawaii</MenuItem>
                    <MenuItem value="ID">ID - Idaho</MenuItem>
                    <MenuItem value="IL">IL - Illinois</MenuItem>
                    <MenuItem value="IN">IN - Indiana</MenuItem>
                    <MenuItem value="IA">IA - Iowa</MenuItem>
                    <MenuItem value="KS">KS - Kansas</MenuItem>
                    <MenuItem value="KY">KY - Kentucky</MenuItem>
                    <MenuItem value="LA">LA - Louisiana</MenuItem>
                    <MenuItem value="ME">ME - Maine</MenuItem>
                    <MenuItem value="MD">MD - Maryland</MenuItem>
                    <MenuItem value="MA">MA - Massachusetts</MenuItem>
                    <MenuItem value="MI">MI - Michigan</MenuItem>
                    <MenuItem value="MN">MN - Minnesota</MenuItem>
                    <MenuItem value="MS">MS - Mississippi</MenuItem>
                    <MenuItem value="MO">MO - Missouri</MenuItem>
                    <MenuItem value="MT">MT - Montana</MenuItem>
                    <MenuItem value="NE">NE - Nebraska</MenuItem>
                    <MenuItem value="NV">NV - Nevada</MenuItem>
                    <MenuItem value="NH">NH - New Hampshire</MenuItem>
                    <MenuItem value="NJ">NJ - New Jersey</MenuItem>
                    <MenuItem value="NM">NM - New Mexico</MenuItem>
                    <MenuItem value="NY">NY - New York</MenuItem>
                    <MenuItem value="NC">NC - North Carolina</MenuItem>
                    <MenuItem value="ND">ND - North Dakota</MenuItem>
                    <MenuItem value="OH">OH - Ohio</MenuItem>
                    <MenuItem value="OK">OK - Oklahoma</MenuItem>
                    <MenuItem value="OR">OR - Oregon</MenuItem>
                    <MenuItem value="PA">PA - Pennsylvania</MenuItem>
                    <MenuItem value="RI">RI - Rhode Island</MenuItem>
                    <MenuItem value="SC">SC - South Carolina</MenuItem>
                    <MenuItem value="SD">SD - South Dakota</MenuItem>
                    <MenuItem value="TN">TN - Tennessee</MenuItem>
                    <MenuItem value="TX">TX - Texas</MenuItem>
                    <MenuItem value="UT">UT - Utah</MenuItem>
                    <MenuItem value="VT">VT - Vermont</MenuItem>
                    <MenuItem value="VA">VA - Virginia</MenuItem>
                    <MenuItem value="WA">WA - Washington</MenuItem>
                    <MenuItem value="WV">WV - West Virginia</MenuItem>
                    <MenuItem value="WI">WI - Wisconsin</MenuItem>
                    <MenuItem value="WY">WY - Wyoming</MenuItem>
                    <MenuItem value="DC">DC - District of Columbia</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tax ID / EIN"
                  name="tax_id"
                  value={formData.tax_id}
                  onChange={handleChange}
                  helperText="Tax ID or Employer Identification Number"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  sx={{ mt: 2 }}
                >
                  {saving ? 'Saving...' : organization ? 'Update Organization' : 'Create Organization'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Wialon GPS Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vehicle locations are pulled from{' '}
            <a href="https://hst-api.wialon.us/wialon/ajax.html" target="_blank" rel="noopener noreferrer">
              Wialon Hosting (US)
            </a>
            . Each Wialon unit name (<code>nm</code>) must match the vehicle license plate in this system
            (spaces and punctuation are ignored). When Wialon GPS is enabled, locations sync automatically
            on the interval you choose below — no manual sync required.
          </Typography>

          {wialonMessage && (
            <Alert
              severity={wialonMessage.toLowerCase().includes('fail') || wialonMessage.toLowerCase().includes('error') || wialonMessage.toLowerCase().includes('required') ? 'error' : 'success'}
              sx={{ mb: 2 }}
              onClose={() => setWialonMessage('')}
            >
              {wialonMessage}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="wialon_gps_enabled"
                    checked={formData.wialon_gps_enabled}
                    onChange={handleChange}
                  />
                }
                label="Use Wialon for vehicle GPS (disable driver phone GPS updates)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Wialon access token"
                name="wialon_access_token"
                type="password"
                value={formData.wialon_access_token}
                onChange={handleChange}
                placeholder={wialonTokenConfigured ? 'Token saved — enter a new value to replace' : 'Paste Wialon API token'}
                helperText={
                  wialonTokenConfigured
                    ? 'A token is already saved. Leave blank to keep it, or paste a new token to replace.'
                    : 'Generate a token in Wialon (OAuth / API access). Required when Wialon GPS is enabled.'
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={!formData.wialon_gps_enabled}>
                <InputLabel id="wialon-sync-interval-label">Automatic sync interval</InputLabel>
                <Select
                  labelId="wialon-sync-interval-label"
                  label="Automatic sync interval"
                  name="wialon_sync_interval_seconds"
                  value={formData.wialon_sync_interval_seconds}
                  onChange={handleChange}
                >
                  {WIALON_SYNC_INTERVAL_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={handleSaveWialonSettings}
                  disabled={saving || !organization}
                >
                  {saving ? 'Saving...' : 'Save Wialon settings'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleTestWialon}
                  disabled={testingWialon || (!formData.wialon_access_token && !wialonTokenConfigured)}
                >
                  {testingWialon ? <CircularProgress size={22} /> : 'Test connection'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleSyncWialon}
                  disabled={syncingWialon || !wialonTokenConfigured}
                >
                  {syncingWialon ? <CircularProgress size={22} /> : 'Sync now (manual)'}
                </Button>
              </Box>
            </Grid>
            {wialonPreview?.preview?.length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Wialon units ({wialonPreview.units_found} found, {wialonPreview.matched_vehicle_count} matched)
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {wialonPreview.preview.slice(0, 10).map((row) => (
                    <Typography component="li" variant="body2" key={row.unit_name}>
                      {row.unit_name}
                      {row.matches_vehicle ? ' — matches vehicle' : ' — no matching vehicle'}
                      {!row.has_position ? ' (no GPS position)' : ''}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            )}
            {wialonPreview?.matched_plates?.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Updated plates: {wialonPreview.matched_plates.join(', ')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Container>
    </Layout>
  );
};

export default ManagerOrganization;

